import {
	faArrowLeft,
	faEnvelope,
	faKey,
	faMobileScreen,
	faPhone,
	faSpinner,
	faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	biometricIconFor,
	useBiometricLogin,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDarkPanelAccentStyle } from '../hooks/useDarkPanelAccentStyle';
import { useKeyboardInset } from '../hooks/useKeyboardInset';
import { useMobileAppPromotion } from '../hooks/useMobileAppPromotion';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { triggerLightImpact } from '../lib/haptics';
import { maskPhoneNumber } from '../lib/maskPhone';
import {
	NO_ACCOUNT_CONTACT_KEY,
	codeSentMessageKey,
	resolveNoAccountPrompt,
} from '../lib/signInCodeCopy';
import { telHref } from '../lib/supportContact';
import { usePortalAuthStore } from '../stores/portalAuthStore';
import { usePortalSiteStore } from '../stores/portalSiteStore';
import { StoreBadges } from './StoreBadges';
import { TextMessageDisclosure } from './TextMessageDisclosure';
import { useTenant } from './TenantGate';

/**
 * PD-16471 — identifier first, then a password or a one-time code:
 *   identifier → password ⇄ code-verify
 */
type LoginView = 'identifier' | 'password' | 'code-verify';

type CodeChannel = 'email' | 'sms';

/**
 * Classify what the visitor typed on the first screen. An email address
 * contains '@'; a phone number has at least ten digits and nothing but
 * digits and the usual punctuation.
 */
function identifierChannel(value: string): CodeChannel | null {
	const trimmed = value.trim();
	if (trimmed.includes('@')) {
		return 'email';
	}
	const digits = trimmed.replace(/\D/g, '');
	if (digits.length >= 10 && /^[\d\s()+.-]+$/.test(trimmed)) {
		return 'sms';
	}
	return null;
}

interface LoginModalProps {
	/** Whether the modal is visible */
	isOpen: boolean;
	/** Called when the modal should close (user dismisses or login succeeds) */
	onClose: () => void;
	/** Optional message shown above the form (e.g. "Your session has expired") */
	message?: string | null;
}

/**
 * Login modal that overlays the portal.
 *
 * Unlike a full-screen login page, this renders on top of the existing
 * portal layout so the user never loses their browsing context. Used for:
 * - Clicking "Sign In" in the header
 * - Attempting an auth-required action while not logged in
 * - Session expiry (token refresh failure)
 *
 * PD-16471 — two steps, matching the CRM sign-in:
 * 1. Email address or phone number.
 * 2. The password, or a one-time code sent to that address or number. The
 *    code message also carries a tap-to-sign-in link, so someone reading it
 *    on the device they want to use can skip typing the code.
 *
 * Emailed codes are always available. Texted codes need the tenant's
 * storefront phone sign-in switch (`authOptions.phoneLoginEnabled`).
 *
 * `defaultLoginMethod` ("Password" / "Sign-in code" in the Portal Sites
 * editor) decides what step 2 leads with: 'email' (the default) shows the
 * password with "Email/Text me a code" under it; 'phone' sends the code
 * straight away with "Use my password instead" under the code boxes. Both
 * steps lead with the same "<email or phone> · Not you?" way back.
 */
export function LoginModal({
	isOpen,
	onClose,
	message,
}: LoginModalProps): JSX.Element | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { name: tenantName, authOptions } = useTenant();
	// Keyboard-obscured band of the layout viewport (PD-14766). Raises the
	// scroll wrapper's bottom edge below so every sign-in option can
	// scroll above the open keyboard — position:fixed ignores the body
	// resize Capacitor applies, so without this the wrapper's scroll area
	// extends underneath the keyboard and the bottom controls (Forgot
	// password, Sign in with phone) are unreachable while typing.
	const keyboardInset = useKeyboardInset();
	const brandLogoUrl = usePortalSiteStore((s) => s.site?.logoUrl ?? null);
	const brandLogoUrlLight = usePortalSiteStore(
		(s) => s.site?.logoUrlLight ?? null,
	);
	const loginTheme = usePortalSiteStore((s) => s.site?.loginTheme ?? 'dark');
	const loginBackgroundUrl = usePortalSiteStore(
		(s) => s.site?.loginBackgroundUrl ?? null,
	);
	// Pre-login surface theme (default 'dark'). When 'light' the modal flips
	// to a light glass panel with dark text; the form inputs are already
	// light in both modes. Drives a small set of class tokens below so the
	// swap stays in one place rather than scattered ternaries.
	const isLight = loginTheme === 'light';
	const darkPanelAccentStyle = useDarkPanelAccentStyle(!isLight);
	const themeLogoUrl = isLight
		? (brandLogoUrlLight ?? brandLogoUrl)
		: brandLogoUrl;
	const panelCls = isLight
		? 'bg-white/90 text-default ring-1 ring-black/10'
		: 'bg-black/40 text-white ring-1 ring-white/15';
	const subtitleCls = isLight ? 'text-subtle' : 'text-white/70';
	const labelCls = isLight ? 'text-strong' : 'text-white/85';
	const mutedCls = isLight ? 'text-subtle' : 'text-white/60';
	const closeCls = isLight
		? 'text-faint hover:text-default hover:bg-black/5'
		: 'text-white/60 hover:text-white hover:bg-white/10';
	const headingCls = isLight ? 'text-default' : 'text-white';
	const optionRowCls = isLight
		? 'bg-black/[0.03] border-black/10 text-default'
		: 'bg-white/10 border-white/15 text-white';
	const digitBoxFilledCls = isLight
		? 'border-strong bg-black/[0.03] text-default'
		: 'border-white/25 bg-white/10 text-white';
	const digitBoxEmptyCls = isLight
		? 'border-default bg-black/[0.02] text-default'
		: 'border-white/15 bg-white/5 text-white';
	// The global `input { @apply bg-brand-input }` rule in index.css wins over
	// a plain `bg-white` utility (element selector specificity), so in light
	// mode we force white with `!` to beat the brand-tinted dark input bg that
	// BrandThemeApplier paints on the (always-dark-by-default) login surface.
	const inputCls = isLight
		? '!bg-white border-strong !text-default'
		: 'bg-surface border-white/30';
	// Self-serve registration is per-portal-site (off by default). The
	// "Register" link only renders when the active brand has opted in via
	// the admin editor; the server re-checks anyway, so flipping this
	// client-side does not bypass anything.
	// Coerce to a real boolean: the entity column is `tinyint` (typed
	// boolean), so an older/undeployed crm-api can hand back the NUMBER 0/1.
	// `{isRegistrationEnabled && …}` would then render a stray "0". Boolean()
	// makes the frontend immune regardless of what the bootstrap returns.
	const isRegistrationEnabled = usePortalSiteStore((s) =>
		Boolean(s.site?.isRegistrationEnabled),
	);
	// Customer-service contact info (PD-14363), rendered as a footer under
	// every view — a customer struggling to sign in is exactly who needs it.
	const appPromotion = useMobileAppPromotion();
	const supportContact = usePortalSiteStore(
		(s) => s.site?.supportContact ?? null,
	);
	const salesContact = usePortalSiteStore((s) => s.site?.salesContact ?? null);
	const {
		login,
		requestSignInCode,
		loginWithCode,
		loginWithRefreshToken,
		isLoading,
		isAuthenticated,
		needsClientSelection,
		selectClient,
		availableClients,
	} = usePortalAuth();

	// Code-first storefronts send the code as soon as the visitor continues.
	const codeFirst = authOptions.defaultLoginMethod === 'phone';

	// View state machine: identifier → password ⇄ code-verify
	const [view, setView] = useState<LoginView>('identifier');

	// Step 1: the email address or phone number
	const [identifier, setIdentifier] = useState('');
	// Step 2: password
	const [password, setPassword] = useState('');

	// Code sign-in state
	const [codeChannel, setCodeChannel] = useState<CodeChannel>('email');
	const [smsDigits, setSmsDigits] = useState<Array<string>>([
		'',
		'',
		'',
		'',
		'',
		'',
	]);
	const [codeExpiresAt, setCodeExpiresAt] = useState<number | null>(null);
	const [resendDisabledUntil, setResendDisabledUntil] = useState<number | null>(
		null,
	);
	const [isSendingCode, setIsSendingCode] = useState(false);
	// Single tick counter drives all countdown displays — avoids multiple
	// setState calls per interval which caused cascading re-render warnings
	const [, setTick] = useState(0);

	// Shared state
	const [error, setError] = useState<string | null>(null);
	const [showClientPicker, setShowClientPicker] = useState(false);
	const [selectingClientId, setSelectingClientId] = useState<number | null>(
		null,
	);

	// Biometric (Face ID / Touch ID / fingerprint) support. Only renders
	// any UI when running inside a native Capacitor shell AND the device
	// has a usable biometric enrolled — `isNative` and `isAvailable` both
	// resolve false on the web build, so the storefront never shows
	// biometric chrome to desktop visitors.
	const biometric = useBiometricLogin();
	const showBiometricUi = biometric.isNative && biometric.isAvailable;
	const [enableBiometricNext, setEnableBiometricNext] = useState(false);
	const [isBiometricLoading, setIsBiometricLoading] = useState(false);

	// One-shot biometric quick-login: when the user has previously enabled
	// it on this device, hitting the modal triggers the OS prompt and, on
	// success, hands the stored refresh token to the existing OAuth refresh
	// path. On cancel/failure we silently fall through to the form.
	//
	// Re-entrancy is guarded with a ref, not just the loading state:
	// state updates land a render later, so rapid repeat taps could start
	// a second run before the button disables. Two concurrent runs would
	// burn the single-use refresh token in a race, so the ref closes that
	// window synchronously.
	const biometricLoginInFlightRef = useRef(false);
	const handleBiometricLogin = useCallback(async () => {
		if (!showBiometricUi || biometricLoginInFlightRef.current) return;
		biometricLoginInFlightRef.current = true;
		setIsBiometricLoading(true);
		setError(null);
		try {
			const session = await biometric.tryAutoLogin(`Sign in to ${tenantName}`);
			if (session != null) {
				// Actively exchange the stored refresh token for a session
				// (PD-14601). The previous passive setRefreshToken relied on
				// a refresh effect that never fires while logged out — the
				// prompt succeeded and then nothing happened. On success the
				// rotation handler re-syncs the keychain with the new token.
				const ok = await loginWithRefreshToken(session.refreshToken);
				if (!ok) {
					// Stored token stale/revoked — clear it so the next visit
					// falls straight through to credential login.
					await biometric.disable();
					setError(
						t(
							'Quick sign-in has expired. Please sign in with your password to re-enable it.',
						),
					);
				}
			}
		} catch (e) {
			console.debug('[biometric] auto-login error', e);
		} finally {
			biometricLoginInFlightRef.current = false;
			setIsBiometricLoading(false);
		}
	}, [biometric, showBiometricUi, tenantName, loginWithRefreshToken, t]);

	// Enroll biometric quick sign-in immediately after a successful login,
	// when the user opted in. Called imperatively from the submit handlers
	// (NOT from a render effect): the login sets `isAuthenticated`, whose
	// effect closes the modal and resets the form — an effect-driven enroll
	// raced that teardown and could miss the just-populated refresh token
	// (PD-14772). Here the caller captures the intent + display label BEFORE
	// awaiting the login, and this reads the freshly-stored refresh token
	// after, so neither depends on the modal still being mounted. The label
	// mirrors the sign-in method (typed email, else masked phone — many
	// portal users have no email on file); it is display-only, the credential
	// is always the refresh token.
	const enrollBiometricAfterLogin = useCallback(
		async (label: string): Promise<void> => {
			if (!showBiometricUi || label === '') {
				return;
			}
			const currentRefreshToken = usePortalAuthStore.getState().refreshToken;
			if (currentRefreshToken == null) {
				return;
			}
			const enabled = await biometric.enable({
				email: label,
				refreshToken: currentRefreshToken,
			});
			// PD-14695: confirm a successful enrollment with a light tap.
			if (enabled) {
				triggerLightImpact();
			}
		},
		[showBiometricUi, biometric],
	);

	const identifierRef = useRef<HTMLInputElement>(null);
	const passwordRef = useRef<HTMLInputElement>(null);

	// Auto-focus the appropriate input when modal opens or view changes
	useEffect(() => {
		if (!isOpen) {
			// Reset all form state when modal closes
			setView('identifier');
			setIdentifier('');
			setPassword('');
			setCodeChannel('email');
			setSmsDigits(['', '', '', '', '', '']);
			setSmsRawInput('');
			setCodeExpiresAt(null);
			setResendDisabledUntil(null);
			setIsSendingCode(false);
			setError(null);
			setShowClientPicker(false);
			setSelectingClientId(null);
			return;
		}

		const timer = setTimeout(() => {
			if (view === 'identifier') {
				identifierRef.current?.focus();
			} else if (view === 'password') {
				passwordRef.current?.focus();
			} else {
				// code-verify — the only remaining view
				hiddenInputRef.current?.focus();
			}
		}, 100);

		return () => clearTimeout(timer);
	}, [isOpen, view]);

	// PD-17082 — lock the page behind the pop-up while it is open. index.css
	// makes <body> the storefront's scroll container, so the page kept its own
	// scroll bar beside the pop-up's scroll wrapper (two scroll bars side by
	// side on a short screen), and the wheel could scroll the hidden page.
	// Only <body> is locked, never <html>: index.css explains why an html
	// overflow lock breaks the Android keyboard. The previous inline value is
	// restored rather than cleared, so an overlay underneath that had already
	// locked the page stays locked.
	// This deliberately leaves the pop-up's own layers alone: the backdrop art
	// is a separate fixed layer and only the wrapper above it scrolls
	// (PD-14409), which is what keeps the art in place on a tall form.
	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [isOpen]);

	// If login succeeded and no client selection needed, close the modal
	useEffect(() => {
		if (isOpen && isAuthenticated && !needsClientSelection) {
			// PD-14695: a light tap marks the sign-in landing (fires once —
			// the modal closes with it and the effect can't re-run).
			triggerLightImpact();
			onClose();
		}
	}, [isOpen, isAuthenticated, needsClientSelection, onClose]);

	// After login, if client selection is needed, show the picker
	useEffect(() => {
		if (isOpen && needsClientSelection && availableClients.length > 1) {
			setShowClientPicker(true);
		}
	}, [isOpen, needsClientSelection, availableClients]);

	// Tick every second to drive countdown displays. Only one setState per tick
	// instead of multiple (countdownText + resendCountdown), which was causing
	// cascading re-render warnings between LoginModal and UserMenu.
	useEffect(() => {
		if (view !== 'code-verify') return;

		const interval = setInterval(() => {
			setTick((prev) => prev + 1);

			// Clear resend cooldown when expired
			if (resendDisabledUntil != null && Date.now() >= resendDisabledUntil) {
				setResendDisabledUntil(null);
			}
		}, 1000);

		return () => clearInterval(interval);
	}, [view, resendDisabledUntil]);

	// Derive countdown displays from timestamps on each render (driven by tick)
	const now = Date.now();
	const codeExpiryRemaining =
		codeExpiresAt != null
			? Math.max(0, Math.ceil((codeExpiresAt - now) / 1000))
			: 0;
	const codeExpiryText =
		codeExpiryRemaining > 0
			? `${Math.floor(codeExpiryRemaining / 60)}:${(codeExpiryRemaining % 60).toString().padStart(2, '0')}`
			: '';
	const resendRemaining =
		resendDisabledUntil != null
			? Math.max(0, Math.ceil((resendDisabledUntil - now) / 1000))
			: 0;

	const trimmedIdentifier = identifier.trim();
	const detectedChannel = identifierChannel(identifier);
	// Codes can go to any email address, and to a phone number only where
	// the tenant allows signing in by text.
	const codeAvailable =
		detectedChannel === 'email' ||
		(detectedChannel === 'sms' && authOptions.phoneLoginEnabled);
	// --- Send (or resend) a sign-in code ---
	const sendCode = useCallback(async (): Promise<boolean> => {
		setError(null);
		setIsSendingCode(true);
		try {
			const result = await requestSignInCode(trimmedIdentifier);
			setCodeChannel(result.channel);
			setCodeExpiresAt(Date.now() + result.expiresIn * 1000);
			setResendDisabledUntil(Date.now() + 60_000); // 60s cooldown
			setSmsDigits(['', '', '', '', '', '']);
			setSmsRawInput('');
			return true;
		} catch (err: unknown) {
			const msg = err instanceof Error ? err.message : t('Failed to send code');
			setError(t(msg));
			return false;
		} finally {
			setIsSendingCode(false);
		}
	}, [requestSignInCode, trimmedIdentifier, t]);

	// --- Step 1: continue with the email or phone ---
	const handleIdentifierSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			setError(null);

			if (detectedChannel == null) {
				setError(t('Please enter your email address or phone number.'));
				return;
			}

			if (codeFirst && codeAvailable) {
				if (await sendCode()) {
					setView('code-verify');
				}
				return;
			}

			setView('password');
		},
		[detectedChannel, codeFirst, codeAvailable, sendCode, t],
	);

	// --- Step 2: password ---
	const handlePasswordSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			setError(null);

			if (password.length === 0) {
				setError(t('Please enter your password.'));
				return;
			}

			// Capture the enroll intent + label BEFORE the await — the login
			// success closes/resets the modal, so form state read afterward may
			// be gone (PD-14772). Phone sign-ins are labeled with the masked
			// number; many portal users have no email on file.
			const wantsEnroll = enableBiometricNext;
			const label =
				detectedChannel === 'sms'
					? maskPhoneNumber(trimmedIdentifier)
					: trimmedIdentifier;

			try {
				await login(trimmedIdentifier, password);
				if (wantsEnroll) {
					await enrollBiometricAfterLogin(label);
				}
			} catch (err: unknown) {
				const msg = err instanceof Error ? err.message : t('Login failed');
				setError(t(msg));
			}
		},
		[
			trimmedIdentifier,
			detectedChannel,
			password,
			login,
			t,
			enableBiometricNext,
			enrollBiometricAfterLogin,
		],
	);

	// --- Step 2: switch from the password to a code ---
	const handleUseCode = useCallback(async () => {
		if (await sendCode()) {
			setView('code-verify');
		}
	}, [sendCode]);

	// --- Code: resend ---
	const handleResendCode = useCallback(async () => {
		if (await sendCode()) {
			hiddenInputRef.current?.focus();
		}
	}, [sendCode]);

	// --- Code: verify ---
	const handleVerifyCode = useCallback(
		async (digits: Array<string>) => {
			const code = digits.join('');
			if (code.length !== 6) return;

			// Capture the enroll intent + label BEFORE the await (PD-14772).
			const wantsEnroll = enableBiometricNext;
			const label =
				codeChannel === 'sms'
					? maskPhoneNumber(trimmedIdentifier)
					: trimmedIdentifier;

			setError(null);
			try {
				await loginWithCode(trimmedIdentifier, code);
				if (wantsEnroll) {
					await enrollBiometricAfterLogin(label);
				}
			} catch (err: unknown) {
				const msg = err instanceof Error ? err.message : t('Invalid code');
				setError(t(msg));
				// Clear digits and refocus first input on failure
				setSmsDigits(['', '', '', '', '', '']);
				setSmsRawInput('');
				hiddenInputRef.current?.focus();
			}
		},
		[
			trimmedIdentifier,
			codeChannel,
			loginWithCode,
			t,
			enableBiometricNext,
			enrollBiometricAfterLogin,
		],
	);

	const changeIdentifier = (): void => {
		setError(null);
		setPassword('');
		setSmsDigits(['', '', '', '', '', '']);
		setSmsRawInput('');
		setView('identifier');
	};

	// The way back to step 1, the same on the password and code screens:
	// the email or phone they entered, and "Not you?".
	const identityChip = (
		<button
			type="button"
			onClick={changeIdentifier}
			className="flex items-center gap-2 max-w-full min-w-0 text-sm font-medium text-portal-accent hover:underline"
		>
			<FontAwesomeIcon icon={faArrowLeft} className="text-xs shrink-0" />
			{/* A long address truncates so "Not you?" stays on screen on a
			    phone instead of being pushed past the pop-up's edge. */}
			<span className="truncate min-w-0">{t(trimmedIdentifier)}</span>
			<span className={`shrink-0 whitespace-nowrap ${mutedCls}`}>
				{t('· Not you?')}
			</span>
		</button>
	);

	// --- Single hidden input approach for 6-digit code ---
	// Uses one real <input> for keyboard/autofill/paste, with visual-only
	// digit boxes. This is the standard pattern (Stripe, Auth0, etc.) that
	// avoids all the focus/autofill/backspace issues of 6 separate inputs.
	const [smsRawInput, setSmsRawInput] = useState('');
	const [isCodeFocused, setIsCodeFocused] = useState(false);
	const hiddenInputRef = useRef<HTMLInputElement>(null);

	const handleCodeInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
			setSmsRawInput(digits);

			// Update the visual digit boxes
			const digitArray = digits.split('');
			setSmsDigits(Array.from({ length: 6 }, (_, i) => digitArray[i] ?? ''));

			// Auto-submit when all 6 digits are entered
			if (digits.length === 6) {
				handleVerifyCode(digits.split(''));
			}
		},
		[handleVerifyCode],
	);

	// --- Client picker ---
	const handleSelectClient = useCallback(
		async (clientId: number) => {
			setSelectingClientId(clientId);
			try {
				await selectClient(clientId);
			} catch (err: unknown) {
				const msg =
					err instanceof Error ? err.message : t('Failed to select account');
				setError(t(msg));
				setSelectingClientId(null);
			}
		},
		[selectClient, t],
	);

	if (!isOpen) return null;

	// --- Determine subtitle based on current view ---
	const getSubtitle = (): string => {
		if (showClientPicker) return t('Select your account');
		if (view === 'password') return t('Enter your password');
		if (view === 'code-verify') return t('Enter your sign-in code');
		return t('Sign in to your account');
	};

	// Biometric quick-login — native shells only AND only when the device
	// has an enrolled biometric AND the user previously enabled it in this
	// app. Rendered at the top of the first step so an enrolled user gets
	// the one-tap path before typing anything. Web builds never see it.
	const biometricQuickLogin =
		showBiometricUi && biometric.hasStoredSession ? (
			<button
				type="button"
				onClick={handleBiometricLogin}
				disabled={isBiometricLoading || isLoading}
				className={`w-full flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent/15 hover:bg-portal-accent/25 disabled:opacity-60 border border-portal-accent/40 font-medium rounded-lg transition-colors min-h-touch ${
					isLight ? 'text-portal-accent' : 'text-white'
				}`}
			>
				{isBiometricLoading ? (
					<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
				) : (
					<FontAwesomeIcon icon={biometricIconFor(biometric.biometryType)} />
				)}
				{isBiometricLoading
					? t('Authenticating...')
					: t(`Sign in with ${biometric.biometryName}`)}
			</button>
		) : null;

	// Register link — only visible when the active portal_site has opted into
	// self-serve registration. Navigates to the standalone /register route (a
	// real history entry + deep-linkable URL for tenants' 3rd-party sites)
	// rather than swapping the form in-modal, which had no URL/back-button and
	// clipped the tall form on short viewports. Rendered in BOTH the
	// email/password and phone views: phone-first portals open on the phone
	// view, and a link that only lived in the email view was effectively
	// hidden from new visitors (and from App Review, who must be able to find
	// how a new business applies for an account). PD-16471: shown on the
	// first step, which every visitor sees. PD-17082: also on the password
	// and code steps, which is where someone without an account lands after
	// Continue.
	const registerPrompt = isRegistrationEnabled ? (
		<p className={`text-center text-xs mt-3 ${mutedCls}`}>
			{t("Don't have an account?")}{' '}
			<button
				type="button"
				className="text-portal-accent hover:underline"
				onClick={() => {
					setError(null);
					onClose();
					navigate('/register');
				}}
			>
				{t('Register')}
			</button>
		</p>
	) : null;

	// PD-17082 — the way forward on the code screen for someone without an
	// account: Register when the brand takes registrations, otherwise the
	// contact details in the footer below.
	const noAccountPrompt = resolveNoAccountPrompt({
		isRegistrationEnabled,
		supportContact,
		salesContact,
	});
	const codeScreenNoAccountPrompt =
		noAccountPrompt === 'register' ? (
			registerPrompt
		) : noAccountPrompt === 'contact' ? (
			<p className={`text-center text-xs mt-3 ${mutedCls}`}>
				{t(NO_ACCOUNT_CONTACT_KEY)}
			</p>
		) : null;

	return (
		<>
			{/*
			  Backdrop: its own full-screen layer, fixed to the viewport, so it
			  stays behind the pop-up at every scroll position. When it was one
			  screen tall and pinned to the top of the scrolling content, a
			  pop-up taller than the screen (a phone in landscape, a long
			  account picker) scrolled it away and showed a flat band of color
			  below it. Fixed positioning, not `background-attachment: fixed`,
			  which the iOS WebView ignores (the same approach as RegisterPage).

			  When the active portal_site has a `loginBackgroundUrl`, the brand
			  image fills the layer with a gradient on top for legibility;
			  without one, a plain scrim tints the brand color. The layer's own
			  `bg-brand-login-bg` (the brand primary in both modes, see
			  BrandThemeApplier) shows while the image loads, and the page
			  behind never shows through.

			  pointer-events-none on purpose: this layer sits outside the scroll
			  wrapper, so a touch that landed on it could not scroll the pop-up.
			  Touches go to the wrapper's content layer instead (PD-14766, see
			  below).
			*/}
			<div
				aria-hidden="true"
				className="fixed inset-0 z-50 overflow-hidden pointer-events-none bg-brand-login-bg"
			>
				{loginBackgroundUrl != null && loginBackgroundUrl !== '' ? (
					<>
						<img
							src={loginBackgroundUrl}
							alt=""
							className="absolute inset-0 h-full w-full object-cover"
						/>
						<div
							className={
								isLight
									? 'absolute inset-0 bg-gradient-to-br from-white/85 via-white/70 to-white/85 backdrop-blur-[2px]'
									: 'absolute inset-0 bg-gradient-to-br from-black/70 via-black/55 to-black/70 backdrop-blur-[2px]'
							}
						/>
					</>
				) : (
					<div
						className={
							isLight
								? 'absolute inset-0 bg-white/60 backdrop-blur-sm'
								: 'absolute inset-0 bg-black/50 backdrop-blur-sm'
						}
					/>
				)}
			</div>

			{/* Scroll wrapper. Transparent, so the fixed backdrop shows through
			    it. Same z-index as the backdrop and later in the DOM, so it
			    stacks above it. */}
			<div
				className="fixed inset-0 z-50 overflow-y-auto"
				style={{
					// While a keyboard is up, raise the wrapper's bottom edge to
					// sit on top of it. The scroll container then spans exactly
					// the visible area, so the form either fits above the
					// keyboard or scrolls to reach its bottom controls. (Bottom
					// padding cannot do this: content shorter than the full
					// viewport-height wrapper never overflows into scrollability
					// no matter how much padding is added.)
					bottom: keyboardInset > 0 ? `${keyboardInset}px` : undefined,
				}}
			>
				{/*
				  Content layer: at least as tall as the scroll wrapper and as
				  wide, so every touch on the background lands on an element
				  INSIDE the scroll container. On-device WebKit only starts a
				  scroll from content inside the scroller; touches on the
				  scroller's own box did nothing, and only the form panel
				  scrolled (PD-14766 device feedback). Growing with its content
				  (min-height, not height) also keeps the top of a pop-up taller
				  than the screen reachable, which centering on the scroller
				  itself does not (a centered flex item overflows upward, past
				  where scrolling can reach).

				  Mobile (default): top-anchored so the email/password fields stay
				  visible when the iOS soft keyboard pops up — keyboards eat
				  ~290–336pt at the bottom and a vertically-centered modal would
				  slide its inputs straight under the keyboard.
				  Desktop (md+): center the modal as expected for a sign-in
				  dialog overlaying the page.

				  Not dismissible: tapping outside the panel does nothing; the
				  user must use the close button or complete login.
				*/}
				<div
					className="flex min-h-full items-start md:items-center justify-center px-4 pb-4"
					style={{ paddingTop: 'max(env(safe-area-inset-top), 1.5rem)' }}
				>
					{/* Modal. role="dialog" also tells PullToRefresh that a pull
					    starting here belongs to the pop-up, not the page. */}
					<div
						role="dialog"
						aria-modal="true"
						aria-label={getSubtitle()}
						className={`relative w-full mx-4 ${panelCls} backdrop-blur-2xl rounded-2xl shadow-2xl shadow-black/50 overflow-hidden ${
							view === 'code-verify' && !showClientPicker ? 'max-w-md' : 'max-w-sm'
						}`}
						style={darkPanelAccentStyle}
					>
						{/* Close button */}
						<button
							type="button"
							onClick={onClose}
							className={`absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-lg transition-colors z-10 ${closeCls}`}
							aria-label={t('Close')}
						>
							<FontAwesomeIcon icon={faTimes} className="text-sm" />
						</button>

						<div className="p-6">
							{/* Brand */}
							<div className="text-center mb-6">
								{themeLogoUrl != null && themeLogoUrl !== '' ? (
									// Logo is the brand identity — don't double-print
									// the tenant name as text underneath it.
									<div className="mb-2 flex justify-center">
										<img
											src={themeLogoUrl}
											alt={t(tenantName)}
											className="h-16 sm:h-20 max-w-[70%] w-auto object-contain"
										/>
									</div>
								) : (
									<h2 className={`text-xl font-bold ${headingCls}`}>
										{t(tenantName)}
									</h2>
								)}
								<p className={`mt-1 text-sm ${subtitleCls}`}>{getSubtitle()}</p>
							</div>

							{/* Session expired or custom message */}
							{message != null && (
								<div
									// White on the solid fill in BOTH themes — same-hue text
									// on the saturated banner was unreadable (owner report,
									// 2026-08-24).
									className="mb-4 bg-warning-solid border border-warning-tint text-sm rounded-lg px-4 py-3 text-white"
								>
									{t(message)}
								</div>
							)}

							{/* Error */}
							{error != null && (
								<div className="mb-4 bg-danger-solid border border-danger-tint text-sm rounded-lg px-4 py-3 text-white">
									{error}
								</div>
							)}

							{/* Client picker (shown after login if multi-client) */}
							{showClientPicker ? (
								<div className="space-y-2">
									{availableClients.map((client) => (
										<button
											key={client.id}
											type="button"
											onClick={() => handleSelectClient(client.id)}
											disabled={isLoading}
											className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border hover:border-portal-accent transition-all min-h-touch disabled:opacity-50 text-left ${optionRowCls}`}
										>
											<div className="flex-1 min-w-0">
												<div
													className={`text-sm font-medium truncate ${headingCls}`}
												>
													{t(client.name ?? '')}
												</div>
												{client.externalId != null && (
													<div className={`text-xs ${mutedCls}`}>
														{t(client.externalId)}
													</div>
												)}
											</div>
											{selectingClientId === client.id && (
												<FontAwesomeIcon
													icon={faSpinner}
													className="animate-spin text-portal-accent shrink-0"
												/>
											)}
										</button>
									))}
								</div>
							) : view === 'identifier' ? (
								/* === Step 1: email address or phone number === */
								<form onSubmit={handleIdentifierSubmit} className="space-y-4">
									{biometricQuickLogin}

									<div>
										<label
											htmlFor="login-identifier"
											className={`block text-sm font-medium mb-1.5 ${labelCls}`}
										>
											{authOptions.phoneLoginEnabled
												? t('Email or phone number')
												: t('Email')}
										</label>
										{/* autocomplete="username" lets password managers offer a
										    saved login here and fill the password on step 2. */}
										<input
											ref={identifierRef}
											id="login-identifier"
											type="text"
											autoComplete="username"
											autoCapitalize="none"
											autoCorrect="off"
											spellCheck={false}
											inputMode="email"
											required
											value={identifier}
											onChange={(e) => setIdentifier(e.target.value)}
											placeholder={
												authOptions.phoneLoginEnabled
													? t('you@example.com or (555) 123-4567')
													: t('you@example.com')
											}
											className={`w-full px-3 py-3 rounded-lg border ${inputCls} text-sm text-default placeholder-subtle focus:ring-2 focus:ring-portal-accent focus:border-transparent shadow-sm`}
											disabled={isLoading || isSendingCode}
										/>
									</div>

									<button
										type="submit"
										disabled={isLoading || isSendingCode}
										className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent hover:bg-portal-accent-hover disabled:opacity-60 text-white font-medium rounded-lg transition-colors min-h-touch"
									>
										{isSendingCode && (
											<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
										)}
										{isSendingCode ? t('Sending code...') : t('Continue')}
									</button>

									{/* PD-17137 — Continue texts a code for a mobile number here, so
									    the texting disclosure shows as soon as one is entered. */}
									{codeFirst && codeAvailable && detectedChannel === 'sms' && (
										<TextMessageDisclosure
											className={`text-xs text-center ${mutedCls}`}
										/>
									)}

									{registerPrompt}
								</form>
							) : view === 'password' ? (
								/* === Step 2: password === */
								<form onSubmit={handlePasswordSubmit} className="space-y-4">
									{identityChip}

									{/* The identifier again, hidden, so a password manager saves
									    and fills this login under the right name. */}
									<input
										type="text"
										autoComplete="username"
										value={trimmedIdentifier}
										readOnly
										tabIndex={-1}
										aria-hidden="true"
										className="sr-only"
									/>

									<div>
										<label
											htmlFor="login-password"
											className={`block text-sm font-medium mb-1.5 ${labelCls}`}
										>
											{t('Password')}
										</label>
										<input
											ref={passwordRef}
											id="login-password"
											type="password"
											autoComplete="current-password"
											autoCapitalize="none"
											autoCorrect="off"
											spellCheck={false}
											required
											value={password}
											onChange={(e) => setPassword(e.target.value)}
											placeholder={t('Enter your password')}
											className={`w-full px-3 py-3 rounded-lg border ${inputCls} text-sm text-default placeholder-subtle focus:ring-2 focus:ring-portal-accent focus:border-transparent shadow-sm`}
											disabled={isLoading}
										/>
									</div>

									{/* Biometric opt-in for next time — native shells only AND
									    only when no session is currently stored. Hides on
									    web automatically because showBiometricUi is false. */}
									{showBiometricUi && !biometric.hasStoredSession && (
										<label
											className={`flex items-center gap-2 text-sm cursor-pointer select-none ${labelCls}`}
										>
											<input
												type="checkbox"
												checked={enableBiometricNext}
												onChange={(e) => setEnableBiometricNext(e.target.checked)}
												className="h-4 w-4 rounded border-white/30 bg-white/10 text-portal-accent focus:ring-2 focus:ring-portal-accent focus:ring-offset-0"
												disabled={isLoading}
											/>
											<span>
												{t(`Enable ${biometric.biometryName} for next sign-in`)}
											</span>
										</label>
									)}

									<button
										type="submit"
										disabled={isLoading}
										className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent hover:bg-portal-accent-hover disabled:opacity-60 text-white font-medium rounded-lg transition-colors min-h-touch"
									>
										{isLoading && (
											<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
										)}
										{isLoading ? t('Signing in...') : t('Sign In')}
									</button>

									{/* Forgot-password link. Closes the modal first so the
									    user lands on /forgot-password without an overlay
									    floating on top. */}
									<div className="text-center">
										<a
											href="/forgot-password"
											onClick={(e) => {
												e.preventDefault();
												onClose();
												window.location.href = '/forgot-password';
											}}
											className="text-sm text-portal-accent hover:underline"
										>
											{t('Forgot your password?')}
										</a>
									</div>

									{/* PD-16471 — the passwordless alternative, by the channel
									    the visitor identified with. */}
									{codeAvailable && (
										<div className="pt-2 border-t border-white/10">
											<button
												type="button"
												onClick={() => {
													void handleUseCode();
												}}
												disabled={isSendingCode}
												className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm transition-colors hover:text-portal-accent disabled:opacity-60 ${mutedCls}`}
											>
												<FontAwesomeIcon
													icon={
														isSendingCode
															? faSpinner
															: detectedChannel === 'sms'
																? faMobileScreen
																: faEnvelope
													}
													className={isSendingCode ? 'animate-spin' : undefined}
												/>
												{detectedChannel === 'sms'
													? t('Text me a sign-in code instead')
													: t('Email me a sign-in code instead')}
											</button>
											{/* PD-17137 — this button texts a code to a mobile number. */}
											{detectedChannel === 'sms' && (
												<TextMessageDisclosure
													className={`mt-1 text-xs text-center ${mutedCls}`}
												/>
											)}
										</div>
									)}

									{registerPrompt}
								</form>
							) : (
								/* === Code verification === */
								<div className="space-y-4">
									{identityChip}
									{/* PD-17082 — conditional on purpose: the code request
									    answers the same way whether or not an account exists,
									    so this screen cannot know a code was sent. */}
									<p className={`text-sm text-center ${mutedCls}`}>
										{t(codeSentMessageKey(codeChannel))}
									</p>
									<p className={`text-xs text-center ${mutedCls}`}>
										{codeChannel === 'sms'
											? t(
													'On this phone? You can tap the link in the text instead.',
												)
											: t(
													'On this device? You can tap Sign In in the email instead.',
												)}
									</p>

									{/* 6-digit code input — single hidden input with visual digit boxes.
									    This is the standard OTP pattern (Stripe, Auth0) that handles
									    autofill, paste, and backspace naturally without focus juggling. */}
									<div className="relative">
										{/* Hidden input captures all keyboard/autofill/paste events */}
										<input
											ref={hiddenInputRef}
											type="text"
											inputMode="numeric"
											autoComplete="one-time-code"
											data-lpignore="true"
											data-1p-ignore="true"
											data-bwignore="true"
											data-form-type="other"
											pattern="[0-9]*"
											maxLength={6}
											value={smsRawInput}
											onChange={handleCodeInputChange}
											disabled={isLoading}
											onFocus={() => setIsCodeFocused(true)}
											onBlur={() => setIsCodeFocused(false)}
											className="absolute inset-0 w-full h-full cursor-pointer"
											style={{ opacity: 0 }}
											aria-label={t('Verification code')}
										/>
										{/* Visual digit boxes — clicking anywhere focuses the hidden input */}
										<div
											className="flex justify-center gap-3"
											onClick={() => hiddenInputRef.current?.focus()}
										>
											{smsDigits.map((digit, i) => {
												const isCursor = isCodeFocused && smsRawInput.length === i;
												const isFilled = digit !== '';
												const isFocusedGroup = isCodeFocused;

												return (
													<div
														key={i}
														className={`w-12 h-14 flex items-center justify-center text-2xl font-semibold rounded-lg border-2 transition-all ${
															isCursor
																? `border-portal-accent shadow-md shadow-portal-accent/20 ${digitBoxFilledCls}`
																: isFocusedGroup && !isFilled
																	? `border-portal-accent/60 ${digitBoxFilledCls}`
																	: isFilled
																		? digitBoxFilledCls
																		: digitBoxEmptyCls
														}`}
													>
														{isFilled ? (
															digit
														) : isCursor ? (
															<div className="w-0.5 h-6 bg-portal-accent animate-pulse rounded-full" />
														) : null}
													</div>
												);
											})}
										</div>
									</div>
									{/* One fixed-height slot under the code boxes for the hint
									    and the "Verifying..." line. The hint used to appear and
									    disappear with focus, which moved everything below it:
									    tapping a button below the boxes blurred them, the page
									    jumped, and the tap landed on something else. Hiding it
									    with `invisible` keeps its space (PD-16471 follow-up). */}
									<div className="mt-2 h-5 flex items-center justify-center">
										{isLoading ? (
											<div className="flex items-center justify-center gap-2 text-sm text-portal-accent">
												<FontAwesomeIcon
													icon={faSpinner}
													className="animate-spin"
												/>
												{t('Verifying...')}
											</div>
										) : (
											<p
												className={`text-xs text-center cursor-pointer ${mutedCls} ${
													!isCodeFocused && smsRawInput.length === 0
														? ''
														: 'invisible'
												}`}
												onClick={() => hiddenInputRef.current?.focus()}
											>
												{t('Tap above to enter code')}
											</p>
										)}
									</div>

									{/* Biometric opt-in — same offer as the credentials form,
									    so phone sign-in users can enroll too (PD-14772). */}
									{showBiometricUi && !biometric.hasStoredSession && (
										<label
											className={`flex items-center gap-2 text-sm cursor-pointer select-none ${labelCls}`}
										>
											<input
												type="checkbox"
												checked={enableBiometricNext}
												onChange={(e) => setEnableBiometricNext(e.target.checked)}
												className="h-4 w-4 rounded border-white/30 bg-white/10 text-portal-accent focus:ring-2 focus:ring-portal-accent focus:ring-offset-0"
												disabled={isLoading}
											/>
											<span>
												{t(`Enable ${biometric.biometryName} for next sign-in`)}
											</span>
										</label>
									)}

									{/* Countdown + resend */}
									<div className={`text-center text-sm ${mutedCls}`}>
										{codeExpiryText !== '' && (
											<p>
												{t('Code expires in')} {codeExpiryText}
											</p>
										)}
										<button
											type="button"
											onClick={handleResendCode}
											disabled={isLoading || isSendingCode || resendRemaining > 0}
											className="mt-2 text-portal-accent hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
										>
											{resendRemaining > 0
												? `${t('Resend code in')} ${resendRemaining}s`
												: t('Resend code')}
										</button>
									</div>

									{codeScreenNoAccountPrompt}

									{/* PD-16471 — the password is always one tap away. */}
									<div className="pt-2 border-t border-white/10">
										<button
											type="button"
											onClick={() => {
												setError(null);
												setSmsDigits(['', '', '', '', '', '']);
												setSmsRawInput('');
												setView('password');
											}}
											className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm transition-colors hover:text-portal-accent ${mutedCls}`}
										>
											<FontAwesomeIcon icon={faKey} />
											{t('Use my password instead')}
										</button>
									</div>
								</div>
							)}

							{/* Customer-support footer (PD-14363) — outside the view
							    switch so it renders under every sign-in view. Whoever is
							    stuck on this modal is the exact audience for the
							    helpline. */}
							{supportContact != null && (
								<div
									className={`mt-6 pt-4 border-t text-center text-sm space-y-1 ${
										isLight ? 'border-black/10' : 'border-white/10'
									}`}
								>
									<p className={mutedCls}>{t('Need help?')}</p>
									{supportContact.phone != null && (
										<p>
											<a
												href={telHref(supportContact.phone)}
												className={`inline-flex items-center gap-2 font-semibold hover:underline ${headingCls}`}
											>
												<FontAwesomeIcon icon={faPhone} className="text-xs" />
												{t(supportContact.phone)}
											</a>
										</p>
									)}
									{supportContact.email != null && (
										<p>
											<a
												href={`mailto:${supportContact.email}`}
												className={`inline-flex items-center gap-2 hover:underline ${labelCls}`}
											>
												<FontAwesomeIcon icon={faEnvelope} className="text-xs" />
												{t(supportContact.email)}
											</a>
										</p>
									)}
									{supportContact.businessHours != null && (
										<p className={`text-xs ${mutedCls}`}>
											{t(supportContact.businessHours)}
										</p>
									)}
								</div>
							)}

							{/* General sales contact (PD-15552) — same placement rationale
							    as the helpline above: outside the view switch so it renders
							    under every sign-in view. Its own border only when it is the
							    first block, so the two don't stack double rules. */}
							{salesContact != null && (
								<div
									className={
										supportContact != null
											? 'mt-3 text-center text-sm space-y-1'
											: `mt-6 pt-4 border-t text-center text-sm space-y-1 ${
													isLight ? 'border-black/10' : 'border-white/10'
												}`
									}
								>
									<p className={mutedCls}>{t('Contact sales')}</p>
									{salesContact.phone != null && (
										<p>
											<a
												href={telHref(salesContact.phone)}
												className={`inline-flex items-center gap-2 font-semibold hover:underline ${headingCls}`}
											>
												<FontAwesomeIcon icon={faPhone} className="text-xs" />
												{t(salesContact.phone)}
											</a>
										</p>
									)}
									{salesContact.email != null && (
										<p>
											<a
												href={`mailto:${salesContact.email}`}
												className={`inline-flex items-center gap-2 hover:underline ${labelCls}`}
											>
												<FontAwesomeIcon icon={faEnvelope} className="text-xs" />
												{t(salesContact.email)}
											</a>
										</p>
									)}
								</div>
							)}

							{/* PD-16558 — the storefront's app, under every sign-in view.
							    Hidden inside the app itself and when the portal site
							    links no store. Its own rule only when nothing above
							    drew one. */}
							{appPromotion != null && (
								<div
									className={
										supportContact != null || salesContact != null
											? 'mt-4 flex flex-col items-center gap-2 text-sm'
											: `mt-6 pt-4 border-t flex flex-col items-center gap-2 text-sm ${
													isLight ? 'border-black/10' : 'border-white/10'
												}`
									}
								>
									<p className={mutedCls}>
										{t('Get the {{name}} app', {
											name: t(appPromotion.brandName),
										})}
									</p>
									<StoreBadges
										app={appPromotion.app}
										platform={appPromotion.platform}
										className="justify-center"
									/>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
